import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Amenity {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  capacity: number;
  max_hours: number;
  is_active: boolean;
  color: string;
  // Payment fields (added in 20240322_amenity_payments.sql)
  price_per_hour: number | null;
  price_flat: number | null;
  requires_payment: boolean;
  require_approval: boolean;
  created_by: string | null;
  image_url: string | null;
  location: string | null;
  rules: string | null;
  created_at: string;
}

export interface AmenityBooking {
  id: string;
  org_id: string;
  amenity_id: string;
  user_id: string | null;
  unit_id: string | null;
  title: string;
  start_time: string;
  end_time: string;
  guest_count: number;
  notes: string | null;
  status: "approved" | "cancelled" | "pending";
  // Payment fields
  payment_status: "unpaid" | "pending" | "paid" | "refunded" | "failed" | "waived";
  payment_amount: number;
  stripe_payment_intent_id: string | null;
  stripe_session_id: string | null;
  rejection_reason: string | null;
  created_at: string;
  // joined
  amenity?: Amenity;
  booker?: { full_name: string | null; email: string | null };
  unit?: { unit_number: string } | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Calculate price for a booking given amenity pricing and duration */
export function calcBookingPrice(amenity: Amenity, startTime: string, endTime: string): number {
  if (!amenity.requires_payment) return 0;
  if (amenity.price_flat) return amenity.price_flat;
  if (amenity.price_per_hour) {
    const hours =
      (new Date(endTime).getTime() - new Date(startTime).getTime()) / (1000 * 60 * 60);
    return Math.round(amenity.price_per_hour * hours * 100) / 100;
  }
  return 0;
}

// ─── Amenities list ───────────────────────────────────────────────────────────

export function useAmenities(includeInactive = false) {
  const { activeOrgId } = useAuth();
  return useQuery({
    queryKey: ["amenities", activeOrgId, includeInactive],
    enabled: !!activeOrgId,
    queryFn: async () => {
      let q = supabase
        .from("amenities")
        .select("*")
        .eq("org_id", activeOrgId!);
      if (!includeInactive) q = q.eq("is_active", true);
      q = q.order("name");
      const { data, error } = await q;
      if (error) throw error;
      return data as Amenity[];
    },
  });
}

// ─── Create / Update amenity (available to host, PM, admin) ──────────────────

export function useManageAmenity() {
  const queryClient = useQueryClient();
  const { activeOrgId, user } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (amenity: Partial<Amenity> & { name: string }) => {
      if (!activeOrgId) throw new Error("No active organization");
      if (amenity.id) {
        const { data, error } = await supabase
          .from("amenities")
          .update(amenity)
          .eq("id", amenity.id)
          .select()
          .single();
        if (error) throw error;
        return data;
      }
      const { data, error } = await supabase
        .from("amenities")
        .insert({ ...amenity, org_id: activeOrgId, created_by: user?.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["amenities", activeOrgId] });
      toast({ title: vars.id ? "Amenity updated" : "Amenity created" });
    },
    onError: (e: Error) =>
      toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}

export function useDeleteAmenity() {
  const queryClient = useQueryClient();
  const { activeOrgId } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      // Soft-delete: set is_active = false so booking history is preserved
      const { error } = await supabase
        .from("amenities")
        .update({ is_active: false })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["amenities", activeOrgId] });
      toast({ title: "Amenity removed" });
    },
    onError: (e: Error) =>
      toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}

// ─── Amenity bookings ─────────────────────────────────────────────────────────

export function useAmenityBookings(amenityId?: string, fromDate?: string, toDate?: string) {
  const { activeOrgId } = useAuth();
  return useQuery({
    queryKey: ["amenity_bookings", activeOrgId, amenityId, fromDate, toDate],
    enabled: !!activeOrgId,
    queryFn: async () => {
      let q = supabase
        .from("amenity_bookings")
        .select(`
          *,
          amenity:amenities(*),
          booker:profiles!amenity_bookings_user_id_fkey(full_name, email),
          unit:units!amenity_bookings_unit_id_fkey(unit_number)
        `)
        .eq("org_id", activeOrgId!)
        .neq("status", "cancelled")
        .order("start_time");
      if (amenityId) q = q.eq("amenity_id", amenityId);
      if (fromDate) q = q.gte("start_time", fromDate);
      if (toDate) q = q.lte("start_time", toDate);
      const { data, error } = await q;
      if (error) throw error;
      return data as AmenityBooking[];
    },
  });
}

type RevenueSummaryRow = {
  org_id: string;
  amenity_id: string;
  amenity_name: string;
  created_by: string | null;
  total_bookings: number;
  approved_bookings: number;
  pending_bookings: number;
  paid_bookings: number;
  total_revenue: number;
  pending_revenue: number;
};

/** Revenue summary by amenity for the active org.
 *  Returns [] gracefully if the view doesn't exist yet (migration pending). */
export function useAmenityRevenue() {
  const { activeOrgId } = useAuth();
  return useQuery({
    queryKey: ["amenity_revenue", activeOrgId],
    enabled: !!activeOrgId,
    // Don't retry when the view simply doesn't exist yet
    retry: (failureCount, err: any) => {
      const msg = String(err?.message ?? "");
      if (msg.includes("404") || msg.includes("does not exist") || msg.includes("42P01")) return false;
      return failureCount < 2;
    },
    queryFn: async (): Promise<RevenueSummaryRow[]> => {
      const { data, error } = await supabase
        .from("amenity_revenue_summary")
        .select("*")
        .eq("org_id", activeOrgId!);
      if (error) {
        // View doesn't exist yet (migration pending) — return empty array gracefully
        console.warn("[useAmenityRevenue] amenity_revenue_summary view not available:", error.message);
        return [];
      }
      return data as RevenueSummaryRow[];
    },
  });
}

/** All bookings for amenities created/owned by the current user.
 *  Returns [] gracefully if the created_by column doesn't exist yet (migration pending). */
export function useMyAmenityBookings() {
  const { activeOrgId, user } = useAuth();
  return useQuery({
    queryKey: ["amenity_bookings_mine", activeOrgId, user?.id],
    enabled: !!activeOrgId && !!user?.id,
    retry: (failureCount, err: any) => {
      const msg = String(err?.message ?? "");
      if (msg.includes("created_by") || msg.includes("column") || msg.includes("400")) return false;
      return failureCount < 2;
    },
    queryFn: async () => {
      // created_by is added in migration 20240322 — handle gracefully if not yet applied
      const { data: myAmenities, error: aErr } = await supabase
        .from("amenities")
        .select("id")
        .eq("org_id", activeOrgId!)
        .eq("created_by", user!.id);

      if (aErr) {
        console.warn("[useMyAmenityBookings] created_by column not yet available:", aErr.message);
        return [] as AmenityBooking[];
      }

      if (!myAmenities || myAmenities.length === 0) return [] as AmenityBooking[];

      const amenityIds = myAmenities.map((a) => a.id);

      const { data, error } = await supabase
        .from("amenity_bookings")
        .select(`
          *,
          amenity:amenities(*),
          booker:profiles!amenity_bookings_user_id_fkey(full_name, email),
          unit:units!amenity_bookings_unit_id_fkey(unit_number)
        `)
        .in("amenity_id", amenityIds)
        .neq("status", "cancelled")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as AmenityBooking[];
    },
  });
}

export function useCreateAmenityBooking() {
  const queryClient = useQueryClient();
  const { user, activeOrgId } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (booking: {
      amenity_id: string;
      unit_id?: string;
      title?: string;
      start_time: string;
      end_time: string;
      guest_count?: number;
      notes?: string;
    }) => {
      if (!activeOrgId) throw new Error("No active organization");

      // Fetch the amenity for pricing and approval settings
      const { data: amenity } = await supabase
        .from("amenities")
        .select("require_approval, requires_payment, price_per_hour, price_flat")
        .eq("id", booking.amenity_id)
        .single();

      // Admin / PM / system_admin → auto-approve
      const isAdminOrPM =
        user?.role === "strata_admin" ||
        user?.role === "property_manager" ||
        user?.role === "system_admin";

      const requireApproval = amenity?.require_approval ?? true;
      const initialStatus =
        isAdminOrPM || !requireApproval ? "approved" : "pending";

      // Calculate payment amount
      let paymentAmount = 0;
      let paymentStatus: "unpaid" | "pending" = "unpaid";
      if (amenity?.requires_payment) {
        if (amenity.price_flat) {
          paymentAmount = amenity.price_flat;
        } else if (amenity.price_per_hour) {
          const hours =
            (new Date(booking.end_time).getTime() - new Date(booking.start_time).getTime()) /
            (1000 * 60 * 60);
          paymentAmount = Math.round(amenity.price_per_hour * hours * 100) / 100;
        }
        paymentStatus = "pending"; // payment required but not yet paid
      }

      // Conflict check — only block against already-approved slots
      const { count } = await supabase
        .from("amenity_bookings")
        .select("id", { count: "exact", head: true })
        .eq("amenity_id", booking.amenity_id)
        .eq("status", "approved")
        .lt("start_time", booking.end_time)
        .gt("end_time", booking.start_time);

      if ((count ?? 0) > 0) throw new Error("This time slot is already booked.");

      const { data, error } = await supabase
        .from("amenity_bookings")
        .insert({
          ...booking,
          org_id: activeOrgId,
          user_id: user?.id,
          title: booking.title ?? "Booking",
          status: initialStatus,
          payment_amount: paymentAmount,
          payment_status: amenity?.requires_payment ? paymentStatus : "waived",
        })
        .select()
        .single();
      if (error) throw error;
      return data as AmenityBooking;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["amenity_bookings", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["amenity_bookings_pending", activeOrgId] });
      const isPending = data?.status === "pending";
      const needsPayment = data?.payment_status === "pending";
      toast({
        title: isPending ? "Booking request submitted" : "Amenity booked",
        description: isPending
          ? "Your request is awaiting approval."
          : needsPayment
          ? "Booking created. Please complete payment to confirm."
          : "Your booking has been confirmed.",
      });
    },
    onError: (e: Error) =>
      toast({ title: "Booking failed", description: e.message, variant: "destructive" }),
  });
}

// ─── Pending bookings (admin/PM/host approval queue) ─────────────────────────

export function usePendingAmenityBookings() {
  const { activeOrgId } = useAuth();
  return useQuery({
    queryKey: ["amenity_bookings_pending", activeOrgId],
    enabled: !!activeOrgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("amenity_bookings")
        .select(`
          *,
          amenity:amenities(*),
          booker:profiles!amenity_bookings_user_id_fkey(full_name, email),
          unit:units!amenity_bookings_unit_id_fkey(unit_number)
        `)
        .eq("org_id", activeOrgId!)
        .eq("status", "pending")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as AmenityBooking[];
    },
  });
}

export function useApproveAmenityBooking() {
  const queryClient = useQueryClient();
  const { activeOrgId } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("amenity_bookings")
        .update({ status: "approved" })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["amenity_bookings", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["amenity_bookings_pending", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["amenity_bookings_mine", activeOrgId] });
      toast({ title: "Booking approved" });
    },
    onError: (e: Error) =>
      toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}

export function useRejectAmenityBooking() {
  const queryClient = useQueryClient();
  const { activeOrgId } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const { error } = await supabase
        .from("amenity_bookings")
        .update({ status: "cancelled", rejection_reason: reason ?? null })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["amenity_bookings", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["amenity_bookings_pending", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["amenity_bookings_mine", activeOrgId] });
      toast({ title: "Booking rejected" });
    },
    onError: (e: Error) =>
      toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}

export function useCancelAmenityBooking() {
  const queryClient = useQueryClient();
  const { activeOrgId } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("amenity_bookings")
        .update({ status: "cancelled" })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["amenity_bookings", activeOrgId] });
      toast({ title: "Booking cancelled" });
    },
    onError: (e: Error) =>
      toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}
